import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PackingSlipDetails from './PackingSlipDetails';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import { useFormik } from 'formik';
import { packingSlipSchema, TAB_ERROR_PREFIXES } from './schemas/indexSchema';
import { client } from '../../../../util';
import type {
  PackingSlip,
  PackingSlipCreateInput,
  ShipMethods,
} from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import {
  closeModal,
  fetchPackingSlipToEdit,
  PackingSlipModalMode,
  setDisableButtons,
  setFormData,
  setLoading,
  setMode,
} from '../../../../state/packingSlipModalSlice';
import DraggablePaper from '../../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { serverLatencyMessage } from '../../constants/messages';
import PackingSlipItems from './PackingSlipItems';
import dayjs from 'dayjs';
import {
  getTabsWithVisibleErrors,
  useFormikTabErrorMiddleware,
  tabErrorSx,
  tabIndicatorErrorSx,
  tabHasError,
} from '../../../utils/FormikTabErorrs';
import SlideUpTransition from '../../SlideUpTransition';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function CreateEditPackingSlipModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tPS } = useTranslation([], {
    keyPrefix: 'team.Modals.Packing Slips.createAndEdit.Add Packing Slip',
  });

  const {
    mode,
    stateIsChanged,
    formData,
    isOpenPackingSlipModal,
    packingSlipIdToEdit,
    updateCallback,
    createCallback,
    loading,
    disableButtons,
  } = useSelector(({ packingSlipModalSlice }) => packingSlipModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const [tab, setTab] = useState(0);

  const dispatch = useDispatch();

  const [shipMethods, setShipMethods] = useState<ShipMethods[]>([]);

  const errorTabTrigger = (tabsWithError: number[]) => {
    if (tabsWithError.length && tabsWithError[0] !== undefined)
      setTab(tabsWithError[0]);
  };

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const updateDisableButtons = (value: boolean) => {
    dispatch(setDisableButtons(value));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  const exitViewMode = async () => {
    dispatch(setMode(PackingSlipModalMode.EDIT));
  };

  const getSuggestedCode = async () => {
    const code = await client.getSuggestedPackingSlipCode.query();
    formik.setFieldValue('code', code);
  };

  const handleSave = async (payload: PackingSlipCreateInput) => {
    updateLoading(true);
    if (mode === PackingSlipModalMode.EDIT) {
      if (packingSlipIdToEdit) {
        try {
          const updatedPackingSlip = await client.updatePackingSlip.mutate({
            ...payload,
            PackingSlipId: packingSlipIdToEdit,
            items: payload.items.map((it: any) => {
              const {
                loading,
                primaryVendor,
                purchaseOrder,
                loadingV,
                loadingPO,
                isNew,
                id,
                ...rest
              } = it;
              return rest;
            }),
          });
          if (updateCallback) updateCallback(updatedPackingSlip);
          dispatch(
            pushToast({
              message: `Packing Slip ${updatedPackingSlip?.code || ''} updated`,
              additionalInfo: serverLatencyMessage,
              type: 'success',
              duration: 7000,
            }),
          );
        } catch (error) {
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
          updateLoading(false);
          return;
        }
      } else {
        console.log(
          'ERROR: Packing slip id is wrong or missing: ',
          packingSlipIdToEdit,
        );
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } else {
      try {
        if (team === 'init' || team === 'loading') return;
        const newPackingSlip: PackingSlip =
          await client.createPackingSlip.mutate({
            ...payload,
            items: payload.items.map((it: any) => {
              const {
                loading,
                primaryVendor,
                purchaseOrder,
                loadingV,
                loadingPO,
                isNew,
                id,
                ...rest
              } = it;
              return rest;
            }),
          });
        if (createCallback) createCallback(newPackingSlip);
        dispatch(
          pushToast({
            message: `Packing Slip ${newPackingSlip?.code || ''} created`,
            additionalInfo: serverLatencyMessage,
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
        updateLoading(false);
        return;
      }
    }
    handleClose();
    updateLoading(false);
  };

  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: false,
    validationSchema: packingSlipSchema,
    onSubmit: handleSave,
  });
  useFormikTabErrorMiddleware(formik, TAB_ERROR_PREFIXES, errorTabTrigger);

  useEffect(() => {
    dispatch(setFormData(formik.values));
  }, [formik.values]);

  const fetchOptions = async () => {
    try {
      const [shipMethodsResp] = await Promise.all([
        client.allShippingMethods.query(),
      ]);
      setShipMethods(shipMethodsResp.results);
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
  };

  const fetchDataForEdit = async () => {
    if (!packingSlipIdToEdit) return;
    try {
      updateLoading(true);
      await fetchOptions();
      const packingSlip = await dispatch(
        fetchPackingSlipToEdit(packingSlipIdToEdit),
      );

      formik.setValues({
        ...formik.values,
        ...packingSlip,
        items: packingSlip.items || [],
      });
      updateLoading(false);
    } catch (error) {
      handleClose();
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } finally {
      updateDisableButtons(false);
      updateLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpenPackingSlipModal) return;
    if (stateIsChanged) return;
    const fetchData = async () => {
      updateLoading(true);
      await Promise.all([fetchOptions(), getSuggestedCode()]);
      if (packingSlipIdToEdit) {
        await Promise.all([fetchDataForEdit()]);
      }
      updateLoading(false);
    };
    formik.setValues(formData);
    fetchData();
  }, [isOpenPackingSlipModal]);

  const renderTitle = () => {
    switch (mode) {
      case PackingSlipModalMode.EDIT:
        return tPS('Edit Packing Slip');
      case PackingSlipModalMode.VIEW:
        return tPS('View Packing Slip');
      case PackingSlipModalMode.CREATE:
        return tPS('Add Packing Slip');
      default:
        return '';
    }
  };

  useEffect(() => {
    if (packingSlipIdToEdit) return;
    if (shipMethods.length > 0) {
      const defaultShipMethod = shipMethods.find((sM) => sM.default);
      if (defaultShipMethod) {
        formik.setFieldValue('shipMethod', defaultShipMethod.ShipMethodId);
      }
    }
  }, [shipMethods]);

  const tabsWithError = getTabsWithVisibleErrors(
    TAB_ERROR_PREFIXES,
    formik.errors,
    formik.touched,
  );

  return (
    <Dialog
      open={isOpenPackingSlipModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      TransitionComponent={SlideUpTransition}
      maxWidth="xl"
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {renderTitle()}
      </DialogTitle>
      <IconButton
        disabled={loading || disableButtons}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-around',
                  },
                }}
                TabIndicatorProps={{
                  sx: tabIndicatorErrorSx(tabHasError(tab, tabsWithError)),
                }}
              >
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(0, tabsWithError)),
                  ]}
                  label={tPS('Details')}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={[
                    { fontWeight: 'bold', width: '100%' },
                    tabErrorSx(tabHasError(1, tabsWithError)),
                  ]}
                  label={tPS('ITEMS')}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <PackingSlipDetails
                formik={formik}
                shipMethods={shipMethods}
                viewMode={mode === PackingSlipModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <PackingSlipItems
                formik={formik}
                viewMode={mode === PackingSlipModalMode.VIEW}
              />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading || disableButtons}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {mode === PackingSlipModalMode.VIEW ? (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                exitViewMode();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Edit')
              )}
            </Button>
          ) : (
            <Button
              disabled={loading || disableButtons}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading || disableButtons ? (
                <CircularProgress size={20} />
              ) : (
                tGlobal('Save')
              )}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
